import { newCorrelationId } from "./correlation";
import { getAccessToken, isAccessExpired, refreshAccessToken, clearTokens } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly correlationId: string,
    public readonly body: unknown,
    message: string,
  ) { super(message); this.name = "ApiError"; }
}

async function attachAuth(headers: Headers): Promise<void> {
  if (isAccessExpired()) await refreshAccessToken();
  const tok = getAccessToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
}

export async function apiFetch<T = unknown>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const correlationId = newCorrelationId();
  const headers = new Headers(init.headers);
  headers.set("X-Correlation-Id", correlationId);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  await attachAuth(headers);

  let res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
      res = await fetch(input, { ...init, headers });
    } else {
      clearTokens();
    }
  }
  const body: unknown = await (res.json() as Promise<unknown>).catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, correlationId, body,
      `API ${res.status} ${typeof body === "object" && body && "title" in body ? (body as { title: string }).title : ""}`.trim());
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Convenience wrappers used by route-level modules.
// ---------------------------------------------------------------------------

/** Authenticated GET — returns parsed JSON. */
export const get = <T>(url: string): Promise<T> =>
  apiFetch<T>(url);

/** Authenticated POST — returns parsed JSON. */
export const post = <T>(url: string, body: unknown): Promise<T> =>
  apiFetch<T>(url, { method: "POST", body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Raw (unauthenticated) helpers — for public / token-based endpoints that
// must NOT send a bearer token.
// ---------------------------------------------------------------------------

export async function getRaw<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new ApiError(r.status, "", null, `API ${r.status}`);
  return r.json() as Promise<T>;
}

export async function postRaw<T = void>(url: string, body: unknown): Promise<T | void> {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new ApiError(r.status, "", null, `API ${r.status}`);
  if (r.status === 204) return;
  return r.json() as Promise<T>;
}
